import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const DeleteCampaignDialog = ({ open, onClose, campaign, onSuccess }) => {
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { apiCall } = useAuth();

  const handleDelete = async () => {
    if (!confirmChecked) {
      setError('Please confirm that you understand the consequences of deleting this campaign');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await apiCall('delete', `/api/campaigns/${campaign.id}`);

      if (result.success || result.message) {
        // Clear form
        setConfirmChecked(false);
        setError('');
        
        // Call success callback
        if (onSuccess) {
          onSuccess();
        }
        
        // Close dialog
        onClose();
      } else {
        setError(result.error || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Campaign deletion failed:', error);
      setError(error.message || 'Failed to delete campaign');
    }

    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      setConfirmChecked(false);
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6">Delete Campaign</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          Are you sure you want to delete "{campaign?.name}"?
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Warning: This action cannot be undone!
          </Typography>
          <Typography variant="body2">
            Deleting this campaign will permanently remove:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>All campaign data and configuration</li>
            <li>Associated rewards and dealer assignments</li>
            <li>All goals and progress data</li>
            <li>Campaign statistics and history</li>
          </Box>
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              disabled={loading}
              color="error"
            />
          }
          label={
            <Typography variant="body2">
              I understand that this action is permanent and cannot be undone
            </Typography>
          }
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading || !confirmChecked}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Deleting...' : 'Delete Campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCampaignDialog;



